import type { BucketKind } from "@/types";
import type { SyncList, SyncTask } from "@/stores/taskStore";
import { compareKeys } from "@/lib/views/order";

// TaskSerializer's JSON: what pages and components receive as task props.
export interface TaskJson {
  id: string;
  title: string;
  status: "pending" | "completed";
  position: string;
  myDayPosition: string | null;
  myDaySequential: boolean;
  completedAt: string | null;
  notes: string | null;
  listId: string;
  listName: string | null;
  bucket: BucketKind | null;
  parentId: string | null;
  childrenCount: number;
  completedChildrenCount: number;
  nextSubtaskTitle: string | null;
  createdAt: string;
  dueOn: string | null;
  myDayOn: string | null;
  myDayDismissedOn: string | null;
  recurrenceRule: string | null;
  reusable: boolean;
  visibleFrom: string | null;
}

export interface ChildrenCount {
  total: number;
  completed: number;
  nextOpen: { title: string; position: string } | null;
}

// Mirrors Task.children_counts_for and Task.next_subtask_titles_for over every parent at once
// (the store holds kept rows only).
export function childrenCounts(tasks: SyncTask[]): Map<string, ChildrenCount> {
  const counts = new Map<string, ChildrenCount>();
  for (const task of tasks) {
    if (task.parentId === null || task.discardedAt) continue;
    const count = counts.get(task.parentId) ?? { total: 0, completed: 0, nextOpen: null };
    count.total += 1;
    if (task.status === "completed") count.completed += 1;
    else if (count.nextOpen === null || compareKeys(task.position, count.nextOpen.position) < 0) {
      count.nextOpen = { title: task.title, position: task.position };
    }
    counts.set(task.parentId, count);
  }
  return counts;
}

export interface TaskDataContext {
  today: string;
  listsById: Map<string, SyncList>;
  childrenCounts: Map<string, ChildrenCount>;
}

const NO_CHILDREN: ChildrenCount = { total: 0, completed: 0, nextOpen: null };

export function toTaskData(task: SyncTask, ctx: TaskDataContext): TaskJson {
  const list = ctx.listsById.get(task.listId);
  const children = ctx.childrenCounts.get(task.id) ?? NO_CHILDREN;
  return {
    id: task.id,
    title: task.title,
    status: task.status,
    position: task.position,
    myDayPosition: task.myDayPosition,
    myDaySequential: task.myDaySequentialOn === ctx.today,
    completedAt: task.completedAt,
    notes: task.notes,
    listId: task.listId,
    listName: list?.name ?? null,
    bucket: list?.kind ?? null,
    parentId: task.parentId,
    childrenCount: children.total,
    completedChildrenCount: children.completed,
    nextSubtaskTitle: children.nextOpen?.title ?? null,
    createdAt: task.createdAt,
    dueOn: task.dueOn,
    myDayOn: task.myDayOn,
    myDayDismissedOn: task.myDayDismissedOn,
    recurrenceRule: task.recurrenceRule,
    reusable: task.reusable,
    visibleFrom: task.visibleFrom,
  };
}
